import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, BookOpen, AlertTriangle, ShieldOff, Sparkles, Database, TrendingDown, TrendingUp } from "lucide-react";
import api from "@/lib/api";

/**
 * MovementFindingsCard
 * ─────────────────────
 * Renders the V1 Intelligence Engine output for the current athlete (or for
 * the `playerUserId` the coach is viewing). Implements the "Why-pane" UX
 * pattern from blueprint §8: every finding leads with the cause (mechanism),
 * then the throwing impact, then the matched inputs, then the cited
 * literature, then the auto-prescribed phases.
 *
 * Coach role gets per-finding override actions (accept / dismiss / downgrade).
 */
export default function MovementFindingsCard({ playerUserId = null, role = "player", className = "" }) {
  const [findings, setFindings] = useState([]);
  const [doNotPrescribe, setDnp] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [pitchSignal, setPitchSignal] = useState(null);  // {peak, avg, baselinePeak, baselineAvg, source, flags}

  const load = async () => {
    setLoading(true);
    try {
      const q = playerUserId ? `?player_user_id=${playerUserId}` : "";
      const { data } = await api.get(`/movement/findings${q}`);
      setFindings(data.findings || []);
      // Pull do_not_prescribe from a fresh evaluate so the gate list is current.
      const { data: ev } = await api.post("/movement/findings/evaluate",
        { player_user_id: playerUserId || undefined, persist: false });
      setDnp(ev.do_not_prescribe || []);

      // ── Telemetry signal: link vendor ball-flight to kinematic findings ──
      // Pull this athlete's most-recent pitch_mechanics capture + linked
      // FlightScope / TrackMan / Rapsodo rollups. If we have at least one
      // ingestion, surface "avg velo 90.1 mph (−2.4 from baseline)" so the
      // coach can correlate kinematic flags with ball-flight drift.
      try {
        const url = playerUserId
          ? `/players/${playerUserId}/pitch-mechanics/latest`
          : `/screening/pitch-mechanics/latest`;
        const { data: pm } = await api.get(url);
        const ings = pm?.telemetry || [];
        if (ings.length) {
          // Current = most-recent ingestion.
          const cur = ings[0]?.summary || {};
          // Baseline = average of all PRIOR ingestions across all captures
          // for this athlete. We pull from /ingestions for the rich history.
          let baselinePeak = null, baselineAvg = null;
          try {
            const histUrl = playerUserId
              ? `/players/${playerUserId}/ingestions`
              : `/players/me/ingestions`;
            const { data: hist } = await api.get(histUrl);
            const prior = (hist?.items || hist || []).filter(
              (h) => h.summary?.avg_velocity_mph != null && h.ingestion_id !== ings[0]?.ingestion_id
            ).slice(0, 10);
            if (prior.length) {
              baselinePeak = avgOf(prior.map((h) => h.summary.max_velocity_mph).filter((x) => x != null));
              baselineAvg = avgOf(prior.map((h) => h.summary.avg_velocity_mph).filter((x) => x != null));
            }
          } catch {/* no history */}
          setPitchSignal({
            source: cur.source,
            peak: cur.max_velocity_mph,
            avg: cur.avg_velocity_mph,
            spin: cur.avg_spin_rpm,
            extension: cur.avg_extension_ft,
            pitches: cur.pitches,
            baselinePeak,
            baselineAvg,
            captureFlags: (pm?.capture?.data?.flags || []).length,
          });
        } else {
          setPitchSignal(null);
        }
      } catch {/* no capture or athlete has no telemetry */}
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [playerUserId]);

  const override = async (finding_id, action, reason = null) => {
    await api.post(`/movement/findings/${finding_id}/override`,
      { action, reason });
    load();
  };

  const sevColor = (c) => c >= 0.95 ? "#D90429" : c >= 0.85 ? "#F59E0B" : "#FBBF24";

  if (loading) {
    return <div className={`${className} text-white/40 text-sm py-6 text-center`}>Loading movement intelligence…</div>;
  }

  if (!findings.length) {
    return (
      <div className={className}>
        <TelemetrySignalCard signal={pitchSignal} />
        <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-6 text-center" data-testid="findings-empty">
          <Sparkles className="h-6 w-6 mx-auto text-emerald-400 mb-2" />
          <div className="text-white font-display font-bold">No open movement findings</div>
          <div className="text-xs text-white/55 mt-1">Complete a baseline screening to populate this view.</div>
        </Card>
      </div>
    );
  }

  return (
    <div className={`${className} space-y-3`} data-testid="findings-card">
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--brand-secondary)]">Intelligence Engine</div>
          <div className="text-lg font-display font-black text-white mt-1">{findings.length} open finding{findings.length === 1 ? "" : "s"}</div>
        </div>
        <button onClick={load} className="text-[10px] uppercase tracking-wider text-white/40 hover:text-white" data-testid="findings-refresh">Refresh</button>
      </div>

      <TelemetrySignalCard signal={pitchSignal} />

      {/* Do-not-prescribe banner */}
      {doNotPrescribe.length > 0 && (
        <div className="rounded-lg p-3 border border-red-500/30 bg-red-500/5" data-testid="findings-dnp">
          <div className="flex items-center gap-2 text-red-300 text-xs font-bold mb-1">
            <ShieldOff className="h-3.5 w-3.5" /> Do not prescribe today
          </div>
          <div className="flex flex-wrap gap-1.5">
            {doNotPrescribe.map((d) => (
              <span key={d} className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-red-500/10 text-red-200 border border-red-500/30">
                {d.replace(/_/g, " ")}
              </span>
            ))}
          </div>
        </div>
      )}

      {findings.map((f) => {
        const open = openId === f.finding_id;
        return (
          <Card key={f.finding_id} className="bg-[var(--ink-2)] border-[var(--ink-border)] overflow-hidden" data-testid={`finding-${f.rule_id}`}>
            <button onClick={() => setOpenId(open ? null : f.finding_id)}
                    className="w-full text-left p-4 flex items-center gap-3 hover:bg-white/[0.02]">
              <div className="flex items-center justify-center h-9 w-12 rounded-md font-display font-black text-xs tabular-nums"
                   style={{ backgroundColor: `${sevColor(f.confidence)}20`, color: sevColor(f.confidence) }}>
                {Math.round((f.confidence || 0) * 100)}%
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/45">{(f.capacity || "").replace(/_/g, " ")}</div>
                <div className="text-white font-bold truncate">{f.rule_id.replace(/_/g, " ")}</div>
              </div>
              {open ? <ChevronDown className="h-5 w-5 text-white/40" /> : <ChevronRight className="h-5 w-5 text-white/40" />}
            </button>

            {open && (
              <div className="px-4 pb-4 border-t border-white/5 pt-3 space-y-3" data-testid={`why-pane-${f.rule_id}`}>
                {/* MECHANISM (the WHY — leads) */}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--brand-secondary)] mb-1">Mechanism · Why</div>
                  <p className="text-sm text-white/80 leading-relaxed">{f.mechanism}</p>
                </div>

                {/* THROWING IMPACT */}
                {f.throwing_impact && (
                  <div className="rounded p-3 bg-black/30 border border-white/5">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-300 mb-1">Throwing impact</div>
                    <p className="text-sm text-white/80 leading-relaxed">{f.throwing_impact}</p>
                  </div>
                )}

                {/* INPUTS THAT FIRED */}
                {f.inputs_matched && Object.keys(f.inputs_matched).length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/50 mb-1">Test inputs that fired this rule</div>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(f.inputs_matched).map(([k, v]) => (
                        <span key={k} className="text-[11px] font-mono px-2 py-1 rounded bg-cyan-500/10 border border-cyan-500/30 text-cyan-200">
                          {k} = {typeof v === "number" ? v : String(v)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* PRESCRIPTION */}
                {Array.isArray(f.prescribe) && f.prescribe.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-300 mb-1">Auto-prescribed phases</div>
                    <div className="space-y-2">
                      {f.prescribe.map((p, i) => (
                        <div key={i} className="rounded p-2.5 bg-emerald-500/5 border border-emerald-500/20">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-200">Phase {p.phase}</span>
                            {p.duration_weeks > 0 && <span className="text-[10px] text-white/50">{p.duration_weeks}w</span>}
                            {p.label && <span className="text-xs text-white/70">{p.label}</span>}
                          </div>
                          <div className="text-xs text-white/70 leading-relaxed">
                            {(p.drills || []).map((d) => d.replace(/_/g, " ")).join(" · ")}
                          </div>
                          {p.gate && (
                            <div className="mt-1 text-[10px] text-white/45">
                              Progression gate: {Object.entries(p.gate).map(([k, v]) => `${k} ${Object.entries(v).map(([c, t]) => `${c} ${t}`).join(" ")}`).join(" + ")}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* CITATIONS */}
                {Array.isArray(f.citations) && f.citations.length > 0 && (
                  <div className="flex items-start gap-2 text-xs text-white/45 pt-1">
                    <BookOpen className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                    <div>{f.citations.join(" · ")}</div>
                  </div>
                )}

                {/* COACH OVERRIDE ACTIONS */}
                {role === "coach" && (
                  <div className="flex gap-2 pt-2 border-t border-white/5">
                    <Button size="sm" variant="outline" className="border-emerald-500/40 text-emerald-200"
                            onClick={() => override(f.finding_id, "accept")} data-testid={`finding-${f.rule_id}-accept`}>
                      Accept
                    </Button>
                    <Button size="sm" variant="outline" className="border-amber-500/40 text-amber-200"
                            onClick={() => override(f.finding_id, "downgrade", prompt("Reason for downgrade?") || null)} data-testid={`finding-${f.rule_id}-downgrade`}>
                      Downgrade
                    </Button>
                    <Button size="sm" variant="outline" className="border-red-500/40 text-red-200"
                            onClick={() => override(f.finding_id, "dismiss", prompt("Reason for dismiss?") || null)} data-testid={`finding-${f.rule_id}-dismiss`}>
                      Dismiss
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Card>
        );
      })}

      <div className="text-[10px] text-white/30 text-center pt-2 flex items-center justify-center gap-1.5">
        <AlertTriangle className="h-3 w-3" /> Engine output. Coach review required before any clinical action.
      </div>
    </div>
  );
}

/**
 * TelemetrySignalCard — surfaces FlightScope / TrackMan / Rapsodo ball-flight
 * drift inside the Why-pane. Each finding tells you WHY the body might be
 * limiting performance; this card tells you whether the ball is showing it.
 *
 *   "Avg velo 90.1 mph (−2.4 from 10-session baseline)"
 *
 * The intent is correlation, not causation — the coach sees the kinematic
 * flags AND the ball-flight drift in one place and draws the connection.
 */
function TelemetrySignalCard({ signal }) {
  if (!signal || signal.avg == null) return null;
  const baseAvg = signal.baselineAvg;
  const delta = baseAvg != null && signal.avg != null ? +(signal.avg - baseAvg).toFixed(1) : null;
  const down = delta != null && delta <= -1.0;        // ≥ 1 mph velo regression
  const up   = delta != null && delta >=  1.0;        // ≥ 1 mph improvement
  const Trend = down ? TrendingDown : up ? TrendingUp : null;

  return (
    <Card className={`p-3 border ${
      down ? "bg-amber-500/5 border-amber-500/30"
        : up ? "bg-emerald-500/5 border-emerald-500/30"
        : "bg-[var(--ink-2)] border-[var(--ink-border)]"
    }`} data-testid="telemetry-signal">
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase font-bold text-white/65">
          <Database className="h-3 w-3" /> Linked Vendor Telemetry · {(signal.source || "vendor").toUpperCase()}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-white/40">
          {signal.pitches || 0} pitches · cause↔outcome
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <Metric label="Peak velo" val={signal.peak} unit=" mph" />
        <Metric label="Avg velo" val={signal.avg} unit=" mph"
                delta={delta} down={down} up={up} TrendIcon={Trend} />
        <Metric label="Avg spin" val={signal.spin} unit=" rpm" round />
        <Metric label="Avg ext" val={signal.extension} unit=" ft" />
      </div>
      {baseAvg != null && delta != null && (down || up) && (
        <div className={`mt-2 text-[11px] leading-snug ${down ? "text-amber-200" : "text-emerald-200"}`} data-testid="telemetry-signal-narrative">
          {down ? <>
            Ball-flight is <b>down {Math.abs(delta)} mph</b> vs the {baseAvg.toFixed(1)}-mph baseline.
            {signal.captureFlags > 0 && <> The {signal.captureFlags} kinematic flag{signal.captureFlags === 1 ? "" : "s"} above may explain the drop — review them first.</>}
          </> : <>
            Ball-flight is <b>up {delta} mph</b> vs the {baseAvg.toFixed(1)}-mph baseline. Kinematics are translating to velocity.
          </>}
        </div>
      )}
    </Card>
  );
}

function Metric({ label, val, unit = "", delta = null, down, up, TrendIcon, round }) {
  if (val == null) return null;
  const v = round ? Math.round(val) : val;
  return (
    <div className="rounded p-2 bg-black/30 border border-white/5">
      <div className="text-[10px] text-white/55 uppercase tracking-wider">{label}</div>
      <div className="font-display font-bold text-base mt-0.5 flex items-baseline gap-1.5 text-white">
        {v}<span className="text-[10px] text-white/40">{unit}</span>
        {delta != null && (down || up) && (
          <span className={`text-[10px] font-bold tabular-nums flex items-center gap-0.5 ${down ? "text-amber-300" : "text-emerald-300"}`}>
            {TrendIcon && <TrendIcon className="h-3 w-3" />}{delta > 0 ? "+" : ""}{delta}
          </span>
        )}
      </div>
    </div>
  );
}

function avgOf(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  return +(arr.reduce((a, b) => a + b, 0) / arr.length).toFixed(1);
}
